import React from 'react';

import {pinContent} from 'common/report/queries/app-api/content-client';
import {useTranslation} from 'common/i18n/hooks';
import {Content} from 'common/types/app-api/content';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {LocalStorageKey} from 'common/report/constants';
import {useLocalStorageManager} from 'common/contexts/react/storage-manager-context';

import {TrackingEvent, TrackingSource} from '../tracking';
import {getContentTypeName, performContentAction} from '../helpers';

export type UsePinContentParams = {
  projectId: string | number;
  workspaceId?: string | number;
  userId: number;
  source?: TrackingSource;
};

export function usePinContent({projectId, userId, source}: UsePinContentParams) {
  const i18n = useTranslation();
  const localStorageManager = useLocalStorageManager();

  const seenPinnedContentIdsStorageRef = React.useRef(
    new UserLocalStorageHelper<string[]>({
      namespace: LocalStorageKey.SeenPinnedContentIds,
      context: localStorageManager,
    }),
  );
  const seenPinnedContentIdsStorage = seenPinnedContentIdsStorageRef.current;

  return React.useCallback(
    (content: Content, onSuccess?: (result: Content) => void) => {
      // Toggle pinned status. `YYYY-MM-DD` format
      const pinnedDate = content.pinned_date ? null : new Date().toISOString().substring(0, 10);
      const entityTypeName = getContentTypeName(content, i18n);
      return performContentAction({
        content,
        action: async () => {
          await pinContent({contentId: content.id, projectId, isPinned: Boolean(pinnedDate)});
          return {
            ...content,
            pinned_date: pinnedDate,
          };
        },
        onSuccess: () => {
          onSuccess?.({
            ...content,
            pinned_date: pinnedDate,
          });
          const seenPinnedContentIds = seenPinnedContentIdsStorage.getItem({userId, key: projectId}) || [];
          if (seenPinnedContentIds.indexOf(content.id) === -1) {
            seenPinnedContentIdsStorage.setItem({
              userId,
              key: projectId,
              value: [...seenPinnedContentIds, content.id],
            });
          }
        },
        successMessage: pinnedDate
          ? i18n.t(`sidenav.pin-content-success`, {entityTypeName})
          : i18n.t(`sidenav.unpin-content-success`, {entityTypeName}),
        errorMessage: pinnedDate
          ? i18n.t(`sidenav.pin-content-error`, {entityTypeName})
          : i18n.t(`sidenav.unpin-content-error`, {entityTypeName}),
        successTrackingEvent: pinnedDate ? TrackingEvent.Pinned : TrackingEvent.Unpinned,
        errorTrackingEvent: pinnedDate ? TrackingEvent.Pinned : TrackingEvent.Unpinned,
        source,
      });
    },
    [i18n, projectId, userId, source, seenPinnedContentIdsStorage],
  );
}
